import { notifications } from "@mantine/notifications"
import { Dispatch, SetStateAction } from "react"
import { useTranslation } from "react-i18next"

import { TraceabilityResponses } from "@tfg/types/endpoints/traceability"

import { queryClient } from "~/backend/queryClient"
import { useRemoveCustomFieldProof } from "~/gen/api"
import { CustomFieldValue } from "~/pages/SupplierPortal/TraceabilityRequests/components/CustomFieldInput"
import { showAxiosError } from "~/utils/showAxiosError"

type DocumentStateUpdater = {
  setValue: Dispatch<SetStateAction<CustomFieldValue>>
  setCustomFieldValues: Dispatch<
    SetStateAction<TraceabilityResponses.CustomFieldValue[]>
  >
}

export type RemoveCustomFieldProofHookProps = {
  customFieldValueId: string
  operationId: number
  inputOpen: () => void
  stateUpdaters: DocumentStateUpdater
}

export const useRemoveCustomFieldProofHook = ({
  customFieldValueId,
  operationId,
  inputOpen,
  stateUpdaters,
}: RemoveCustomFieldProofHookProps) => {
  const { t } = useTranslation("traceability")

  const { mutate: removeCustomFieldProof, isLoading: isRemoving } =
    useRemoveCustomFieldProof(customFieldValueId, {
      mutation: {
        onSuccess: async () => {
          stateUpdaters.setValue((prev) => ({
            ...prev,
            selectedFile: null,
            document: null,
          }))
          stateUpdaters.setCustomFieldValues((prev) => {
            return prev.map((field) => {
              if (field.id === customFieldValueId) {
                const updated = {
                  ...field,
                  document: null,
                }
                return updated
              }
              return field
            })
          })

          inputOpen()
          notifications.show({
            message: t(
              "customFields.updateCustomField.customFieldProof.message"
            ),
            title: t("customFields.updateCustomField.customFieldProof.title"),
            color: "green",
          })
          await queryClient.invalidateQueries({
            queryKey: ["listTraceabilityRequests", operationId],
            refetchType: "none",
          })
        },
        onError: async (error) => {
          showAxiosError({
            showNotification: true,
            error,
          })
          await queryClient.invalidateQueries([
            "listTraceabilityRequests",
            operationId,
          ])
        },
      },
    })

  return {
    removeCustomFieldProof,
    isRemoving,
  }
}
